import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionLoader } from "@/components/common";
import { formatDate, formatTime } from "@/lib/format";
import { CalendarDays, MapPin, ArrowRight, Users, Briefcase, Sparkles, Radio, Trophy, Flame, Star, AudioLines, ShieldCheck, UserPlus } from "lucide-react";

export default function Dashboard() {
  const { user, can } = useAuth();
  const [events, setEvents] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/events", { params: { upcoming: true } }).then((r) => r.data).catch(() => []),
      api.get("/gamify/me").then((r) => r.data).catch(() => null),
    ]).then(([ev, rw]) => { setEvents(ev); setRewards(rw); setLoading(false); });
  }, []);

  const nextEvent = events[0];

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Hi {user?.name?.split(" ")[0]} 👋</h1>
        <p className="mt-1 text-muted-foreground">Here's what's happening at Friday Conversations.</p>
      </div>

      {rewards && (
        <Link to="/portal/rewards" className="block" data-testid="dashboard-rewards-widget">
          <Card className="border-border bg-card transition-shadow hover:shadow-[0_18px_50px_rgba(20,20,20,0.10)]">
            <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-4 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10"><Star className="h-5 w-5 text-primary" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">Your level</p>
                  <p className="font-serif text-lg font-semibold text-foreground">{rewards.level?.name}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Points</p>
                <p className="font-serif text-lg font-semibold tabular-nums text-foreground">{rewards.points}</p>
              </div>
              <div className="flex items-center gap-2">
                <Flame className="h-5 w-5 text-[#C9772E]" />
                <div>
                  <p className="text-xs text-muted-foreground">Streak</p>
                  <p className="font-serif text-lg font-semibold tabular-nums text-foreground">{rewards.streak_weeks} wk</p>
                </div>
              </div>
              {rewards.rank && (
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Rank</p>
                    <p className="font-serif text-lg font-semibold tabular-nums text-foreground">#{rewards.rank}</p>
                  </div>
                </div>
              )}
              <span className="ml-auto inline-flex items-center text-sm font-semibold text-primary">View rewards <ArrowRight className="ml-1 h-4 w-4" /></span>
            </CardContent>
          </Card>
        </Link>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Next event */}
        <Card className="lg:col-span-2 border-border bg-card" data-testid="portal-next-event-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Next event</p>
            </div>
            {nextEvent ? (
              <>
                <h2 className="mt-2 font-serif text-2xl font-semibold text-foreground">{nextEvent.title}</h2>
                {nextEvent.theme_title && <p className="mt-1 text-sm font-medium text-primary">Theme: {nextEvent.theme_title}</p>}
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{formatDate(nextEvent.date)} {nextEvent.start_time ? `· ${formatTime(nextEvent.start_time)}` : ""}</span>
                  {nextEvent.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{nextEvent.location.name}</span>}
                </div>
                <Button asChild className="mt-5 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"><Link to={`/events/${nextEvent.event_id}`}>View event <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
              </>
            ) : (
              <p className="mt-4 text-muted-foreground">No upcoming events scheduled yet. Check back soon!</p>
            )}
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card className="border-border bg-card">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick actions</p>
            <div className="mt-3 space-y-2">
              <Button asChild variant="secondary" className="w-full justify-start"><Link to="/portal/hub"><Radio className="mr-2 h-4 w-4" />Podcast Hub (live)</Link></Button>
              <Button asChild variant="secondary" className="w-full justify-start" data-testid="dash-link-invite"><Link to="/portal/invite"><UserPlus className="mr-2 h-4 w-4" />Invite a member</Link></Button>
              <Button asChild variant="secondary" className="w-full justify-start"><Link to="/portal/directory"><Users className="mr-2 h-4 w-4" />Browse member directory</Link></Button>
              <Button asChild variant="secondary" className="w-full justify-start"><Link to="/portal/account/business"><Briefcase className="mr-2 h-4 w-4" />Edit my business</Link></Button>
              {can("producer_tools") && <Button asChild variant="secondary" className="w-full justify-start" data-testid="dash-link-studio"><Link to="/admin/studio/recordings"><AudioLines className="mr-2 h-4 w-4" />Producer Studio</Link></Button>}
              {can("manage_events") && <Button asChild variant="secondary" className="w-full justify-start" data-testid="dash-link-events"><Link to="/admin/manage/events"><CalendarDays className="mr-2 h-4 w-4" />Manage events</Link></Button>}
              {can("view_members") && <Button asChild variant="secondary" className="w-full justify-start" data-testid="dash-link-members"><Link to="/admin/members"><ShieldCheck className="mr-2 h-4 w-4" />Members</Link></Button>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
